/**
 * Editor de alergénios do produto (#325): os 14 da UE (Reg. 1169/2011,
 * Anexo II) como chips. Sugestões da IA chegam pré-marcadas e identificadas
 * pela origem; só "Confirmar" as transforma em facto.
 */
import { useState } from 'react'
import { useMutation, type useQueryClient } from '@tanstack/react-query'
import { products as prodApi, type Product } from '../lib/api.js'
import {
  allergenBadgeState,
  allergenState,
  chipSource,
  hasChanges,
  initialSelection,
  suggestionOf,
  toggleCode,
} from '../lib/allergens.js'
import { Button, toast } from './ui/index.js'
import { AllergenBadge, AllergenChips, AllergenLegend } from './AllergenChips.js'
import { useI18n } from '../lib/i18n.js'

export function AllergensEditor({
  product,
  qc,
  onProductUpdate,
}: {
  product: Product
  qc: ReturnType<typeof useQueryClient>
  onProductUpdate: (p: Product) => void
}) {
  const { t } = useI18n()
  const [selected, setSelected] = useState<number[]>(() => initialSelection(product))
  // produto trocado ou confirmado noutro lado: recomeça da lista do servidor
  const stamp = `${product.id}|${product.allergens_confirmed_at ?? ''}`
  const [prevStamp, setPrevStamp] = useState(stamp)
  if (stamp !== prevStamp) {
    setPrevStamp(stamp)
    setSelected(initialSelection(product))
  }

  const state = allergenState(product)
  const confirmMut = useMutation({
    mutationFn: () => prodApi.confirmAllergens(product.id, selected),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      toast.success(t('admin.menu.allergens_saved'))
      if (data) onProductUpdate(data)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div
      className="rounded-xl border border-gray-200 p-4"
      data-testid="catalog-allergens"
      role="group"
      aria-labelledby="catalog-allergens-label"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p id="catalog-allergens-label" className="text-sm font-medium text-gray-700">
          {t('admin.menu.allergens')}
        </p>
        <AllergenBadge
          state={allergenBadgeState(product)}
          testId="catalog-allergens-state"
          prefix={false}
        />
      </div>
      <p className="text-xs text-gray-400 mb-3">{t('admin.menu.allergens_hint')}</p>
      <div className="mb-3">
        <AllergenChips
          selected={selected}
          sourceOf={(code) => chipSource(suggestionOf(product, code)?.source)}
          onToggle={(code) => setSelected((s) => toggleCode(s, code))}
          testIdPrefix="catalog-allergen-"
        />
      </div>
      {state === 'pending' && (
        <div className="mb-3">
          <AllergenLegend />
        </div>
      )}
      {state === 'unreviewed' && (
        <p className="text-[11px] text-amber-700 mb-3" data-testid="catalog-allergens-unreviewed">
          {t('admin.menu.allergens_unreviewed_hint')}
        </p>
      )}
      {/* "sem alergénios" é o selo `none` (confirmado com lista vazia) */}
      <div className="flex items-center justify-end gap-2">
        <Button
          size="sm"
          variant="secondary"
          data-testid="catalog-allergens-confirm"
          loading={confirmMut.isPending}
          disabled={confirmMut.isPending || !hasChanges(product, selected)}
          onClick={() => confirmMut.mutate()}
        >
          {t('admin.menu.allergens_confirm')}
        </Button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">{t('admin.menu.allergens_reopen_note')}</p>
    </div>
  )
}
